import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import instance from "api/apiClient";
import { apiRoutes } from "configs/apiRoutes";

import { useAppDispatch, useAppSelector } from "store/index";
import { NotificationTypes, showNotification } from "store/notifications/slice";

import RoundBtn from "components/buttons/RoundBtn";

import MainPageSortItem from "./components/MainPageSortItem";
import MainPageDragItem from "./components/MainPageDragItem";

import icon from "assets/icons/burger-icon.svg";
import "./Widgets.scss";
import { selectFilteredGeo } from "store/geoFilter/selectors";
import WidgetsListSkeleton from "components/global/Skeleton/WidgetsListSkeleton/WidgetsListSkeleton";
import { usePermissionHelper } from "helpers/usePermissionHelper";

const Footer: React.FC = () => {
  const { t } = useTranslation();
  const geoList = useAppSelector(selectFilteredGeo);

  const [activeGeo, setActiveGeo] = useState<string>();
  const [pageItems, setPageItems] = useState<any[]>([]);
  const [availableItems, setAvailableItems] = useState<any[]>([]);
  const [itemsFetched, setItemsFetched] = useState(false);
  const [selectedToAdd, setSelectedToAdd] = useState<any[]>([]);
  const [selectedToDelete, setSelectedToDelete] = useState<any[]>([]);
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (activeGeo) {
      loadGeoData(activeGeo);
    }
  }, [activeGeo]);

  useEffect(() => {
    if (geoList.length > 0) {
      setActiveGeo(geoList[0]);
    }
  }, [geoList]);

  const filteredAvailable = useMemo(() => {
    return availableItems.filter(
      (el) => !pageItems.find((item) => item.value === el.value),
    );
  }, [availableItems, pageItems]);

  const loadGeoData = async (geo: string) => {
    setItemsFetched(false);

    const rightRes = await instance.get(`${apiRoutes.footerItems}?geo=${geo}`);

    const rightItems = (rightRes.data.items ?? []).map((el: any) => ({
      value: el.id,
      type: el.type,
      label: el.title,
      img: icon,
      showType: true,
    }));

    setAvailableItems(rightItems);

    const leftRes = await instance.get(`${apiRoutes.footerPage}?geo=${geo}`);

    const leftItems = (leftRes.data.items ?? []).map((el: any) => {
      return {
        value: el.instanceId,
        type: el.instanceType,
        label: el.instanceType,
        img: icon,
        priorityOrder: el.priorityOrder,
        showType: false,
      };
    });

    setPageItems(leftItems);
    setItemsFetched(true);
  };

  const permissions = usePermissionHelper();

  const handleUpdateItems = async (items: any[]) => {
    try {
      setItemsFetched(false);

      await instance.post(apiRoutes.footerUpdate, {
        geo: activeGeo,
        items: items.map((el, idx) => ({
          instanceType: el.type,
          instanceId: el.value,
          priorityOrder: idx + 1,
        })),
      });

      await loadGeoData(activeGeo!);

      dispatch(
        showNotification({
          title: t("success"),
          message: t("itemsUpdated"),
          type: NotificationTypes.success,
        }),
      );
    } catch (e) {
      dispatch(
        showNotification({
          title: t("error"),
          message: t("itemsUpdateError"),
          type: NotificationTypes.error,
        }),
      );
    }
  };

  const moveItem = async (dragIndex: number, hoverIndex: number) => {
    const updated = [...pageItems];
    const [dragged] = updated.splice(dragIndex, 1);
    updated.splice(hoverIndex, 0, dragged);

    try {
      setItemsFetched(false);

      await instance.post(apiRoutes.footerUpdate, {
        geo: activeGeo,
        items: updated.map((el, idx) => ({
          instanceType: el.type,
          instanceId: el.value,
          priorityOrder: idx + 1,
        })),
      });

      await loadGeoData(activeGeo!);

      dispatch(
        showNotification({
          title: t("success"),
          message: t("itemsOrderUpdated"),
          type: NotificationTypes.success,
        }),
      );
    } catch {
      dispatch(
        showNotification({
          title: t("error"),
          message: t("itemsOrderUpdateError"),
          type: NotificationTypes.error,
        }),
      );
    }
  };

  return (
    <div className="main-page-wrapper burger-page-wrapper">
      <div className="main-page-content">
        <div className="main-page-widget">
          <div className="main-page-widget--box">
            <div className="main-page-widget--tabs">
              <div className="game-modal-tabs small">
                {geoList?.map((el) => (
                  <div
                    key={el}
                    className={`tab-item ${activeGeo === el ? "active" : ""}`}
                    onClick={() => setActiveGeo(el)}
                  >
                    {el.toLowerCase() === "ww" ? (
                      <span className="mr-5">🌎</span>
                    ) : (
                      <img
                        src={`https://flagcdn.com/w40/${el.toLowerCase()}.png`}
                        alt={el}
                      />
                    )}
                    {t(el)}
                  </div>
                ))}
              </div>
            </div>

            <div className="main-page-widget--list">
              {itemsFetched ? (
                pageItems.map((el, index) => (
                  <MainPageSortItem
                    key={el.value}
                    item={el}
                    index={index}
                    moveItem={moveItem}
                    isSelected={selectedToDelete.includes(el)}
                    handleClick={() => {
                      if (selectedToDelete.includes(el)) {
                        setSelectedToDelete(
                          selectedToDelete.filter((i) => i !== el),
                        );
                      } else {
                        setSelectedToDelete([...selectedToDelete, el]);
                      }
                    }}
                    isBurger
                  />
                ))
              ) : (
                <WidgetsListSkeleton />
              )}
            </div>

            <div className="main-page-btns">
              {permissions?.create && (
                <RoundBtn
                  txt="+"
                  disabled={selectedToAdd.length === 0}
                  handleClick={() => {
                    const newArray = [...pageItems, ...selectedToAdd];
                    handleUpdateItems(newArray);
                    setSelectedToAdd([]);
                  }}
                />
              )}
              {permissions?.delete && (
                <RoundBtn
                  txt="-"
                  disabled={selectedToDelete.length === 0}
                  handleClick={() => {
                    const newArray = pageItems.filter(
                      (i) => !selectedToDelete.includes(i),
                    );
                    handleUpdateItems(newArray);
                    setSelectedToDelete([]);
                  }}
                />
              )}
            </div>
          </div>
        </div>

        <div className="main-page-widget">
          <div className="main-page-widget--box">
            <div className="main-page-title">{t("menuItems")}</div>
            <div className="main-page-list-content">
              {itemsFetched ? (
                filteredAvailable.map((el) => (
                  <MainPageDragItem
                    key={el.value}
                    item={el}
                    type="footer"
                    isSelected={selectedToAdd.includes(el)}
                    handleClick={() => {
                      if (selectedToAdd.includes(el)) {
                        setSelectedToAdd(selectedToAdd.filter((i) => i !== el));
                      } else {
                        setSelectedToAdd([...selectedToAdd, el]);
                      }
                    }}
                    isBurger
                  />
                ))
              ) : (
                <WidgetsListSkeleton />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Footer;
